// ─── Feuille « ajouter / modifier un accès » (Core) ───────────────
// Ouverte par SectionAcces. En AJOUT : choisir qui (Tout l'espace + membres
// pas encore listés) puis le niveau. En ÉDITION : changer le niveau ou retirer.
// Renvoie via onValider(cle, niveau) ; onRetirer(cle) pour retirer un accès.
import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { useUI } from '../theme/PreferencesUI';
import { useT } from '../i18n/LangueContext';
import { confirmerSuppression } from '../services/confirm';
import Feuille from '../ui/Feuille';
import { NIVEAUX } from './niveaux';

const INFO = {
  lecteur:      { icone: '👁️', label: 'acces.niveauLecteur',      desc: 'acces.niveauLecteurDesc' },
  contributeur: { icone: '✏️', label: 'acces.niveauContributeur', desc: 'acces.niveauContributeurDesc' },
  gestionnaire: { icone: '⚙️', label: 'acces.niveauGestionnaire', desc: 'acces.niveauGestionnaireDesc' },
  aucun:        { icone: '🚫', label: 'acces.niveauAucun',        desc: 'acces.niveauAucunDesc' },
};

export default function FeuilleNiveauAcces({
  visible, mode = 'add', cible, cibleNom, titreRessource,
  membresDispo = [], niveauInit = 'lecteur', niveaux = NIVEAUX,
  onValider, onRetirer, onFermer,
}) {
  const UI = useUI();
  const t = useT();
  const s = useMemo(() => makeStyles(UI), [UI]);
  const [qui, setQui] = useState(null);        // uid | '*' (ajout)
  const [niveau, setNiveau] = useState(niveauInit);

  useEffect(() => {
    if (!visible) return;
    setQui(mode === 'edit' ? cible : null);
    setNiveau(niveauInit || 'lecteur');
  }, [visible, mode, cible, niveauInit]);

  // Candidats en AJOUT : « Tout l'espace » (si pas déjà) + membres non listés.
  const candidats = useMemo(() => {
    const arr = [];
    if (membresDispo.some(m => m.uid === '*')) arr.push({ uid: '*', nom: t('acces.toutEspace'), tous: true });
    membresDispo.filter(m => m.uid !== '*').forEach(m => arr.push({ uid: m.uid, nom: m.nom }));
    return arr;
  }, [membresDispo, t]);

  // « Pas d'accès » = 4e option de la liste (édition seulement) : la disquette
  // RETIRE alors l'accès — plus de gros bouton dédié (principe bouton-icône).
  const options = mode === 'edit' ? [...niveaux, 'aucun'] : niveaux;
  const peutValider = !!qui && !!niveau && !(mode === 'edit' && niveau === niveauInit);

  function valider() {
    if (!peutValider) return;
    if (niveau === 'aucun') {
      confirmerSuppression(
        t('acces.retirerAcces'),
        t('acces.retirerAccesMsg', { nom: cibleNom || '', titre: titreRessource || '' }),
        () => { onRetirer?.(cible); onFermer?.(); },
      );
      return;
    }
    onValider?.(qui, niveau);
    onFermer?.();
  }

  const action = { icone: mode === 'add' ? '＋' : '💾', onPress: valider, disabled: !peutValider, label: t(mode === 'add' ? 'commun.ajouter' : 'commun.enregistrer') };

  return (
    <Feuille visible={visible} titre={t(mode === 'add' ? 'acces.ajoutTitre' : 'acces.modifierTitre')}
      emoji="🔑" hauteur={0.82} onFermer={onFermer} action={action}>

      {mode === 'add' && (
        <>
          <Text style={s.lbl}>{t('acces.qui')}</Text>
          {candidats.length === 0 ? (
            <Text style={s.vide}>{t('acces.aucunMembreDispo')}</Text>
          ) : candidats.map(c => {
            const on = qui === c.uid;
            return (
              <TouchableOpacity key={c.uid} style={[s.opt, on && s.optOn]} onPress={() => setQui(c.uid)}>
                <Text style={s.radio}>{on ? '◉' : '○'}</Text>
                {c.tous
                  ? <Text style={s.optIc}>👥</Text>
                  : <View style={s.avatar}><Text style={s.avatarTxt}>{(c.nom || '?').slice(0, 1).toUpperCase()}</Text></View>}
                <Text style={s.optNom} numberOfLines={1}>{c.nom || c.uid.slice(0, 5)}</Text>
              </TouchableOpacity>
            );
          })}
        </>
      )}

      <Text style={s.lbl}>{t('acces.niveau')}</Text>
      {options.map(n => {
        const info = INFO[n]; const on = niveau === n;
        return (
          <TouchableOpacity key={n} style={[s.niv, on && s.nivOn]} onPress={() => setNiveau(n)}>
            <Text style={s.nivIc}>{info.icone}</Text>
            <View style={{ flex: 1 }}>
              <Text style={s.nivNom}>{t(info.label)}</Text>
              <Text style={s.nivDesc}>{t(info.desc)}</Text>
            </View>
            <Text style={[s.radio, on && { color: UI.accent }]}>{on ? '◉' : '○'}</Text>
          </TouchableOpacity>
        );
      })}

    </Feuille>
  );
}

const makeStyles = (UI) => StyleSheet.create({
  lbl: { fontSize: 12, fontWeight: '700', color: UI.muted, marginTop: 14, marginBottom: 7 },
  vide: { fontSize: 12.5, color: UI.faint, fontStyle: 'italic' },
  opt: { flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 9, paddingHorizontal: 8, borderRadius: 10, marginBottom: 4 },
  optOn: { backgroundColor: UI.accentTint },
  radio: { fontSize: 17, color: UI.muted, width: 20, textAlign: 'center' },
  optIc: { fontSize: 18, width: 26, textAlign: 'center' },
  avatar: { width: 26, height: 26, borderRadius: 13, backgroundColor: UI.accentTint, alignItems: 'center', justifyContent: 'center' },
  avatarTxt: { fontSize: 12, fontWeight: '800', color: UI.accent },
  optNom: { flex: 1, fontSize: 14, fontWeight: '700', color: UI.ink },
  niv: { flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 10, paddingHorizontal: 10, borderRadius: 11, borderWidth: 1, borderColor: UI.border, marginBottom: 7 },
  nivOn: { borderColor: UI.accent, backgroundColor: UI.accentTint },
  nivIc: { fontSize: 18, width: 24, textAlign: 'center' },
  nivNom: { fontSize: 14, fontWeight: '800', color: UI.ink },
  nivDesc: { fontSize: 11.5, color: UI.muted, marginTop: 1, lineHeight: 15 },
});
